export interface TileRect {
  /** Left column (tiles). */
  x: number;
  /** Top row (tiles). */
  y: number;
  /** Width in tiles. */
  w: number;
  /** Height in tiles. */
  h: number;
}

/**
 * Merge solid grid cells into the fewest axis-aligned rectangles that cover
 * them: vertical runs per column, then adjacent columns with identical runs are
 * joined. Used to build collision slabs without interior tile seams. Arcade
 * resolves Y before X, so a body pushing sideways into a seam while rising was
 * treated as hitting a ceiling; slabs remove those seams entirely.
 */
export function mergeSolidRects(
  rows: readonly string[],
  isSolid: (ch: string) => boolean,
): TileRect[] {
  const width = Math.max(0, ...rows.map((row) => row.length));
  const rects: TileRect[] = [];
  // Rects from the previous column that can still grow rightwards.
  let open: TileRect[] = [];
  for (let x = 0; x < width; x += 1) {
    const runs: TileRect[] = [];
    let y = 0;
    while (y < rows.length) {
      if (!isSolid(rows[y]?.[x] ?? ".")) {
        y += 1;
        continue;
      }
      const start = y;
      while (y < rows.length && isSolid(rows[y]?.[x] ?? ".")) y += 1;
      runs.push({ x, y: start, w: 1, h: y - start });
    }
    const next: TileRect[] = [];
    for (const run of runs) {
      const match = open.find((rect) => rect.y === run.y && rect.h === run.h);
      if (match) {
        match.w += 1;
        next.push(match);
      } else {
        rects.push(run);
        next.push(run);
      }
    }
    open = next;
  }
  return rects;
}
